import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Plus, Search, Loader2, Upload, Download, AlertCircle, CheckCircle2, X, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const SPECIALTIES = ['Engine','Brakes','Tires','Electrical','Transmission','HVAC','Suspension','Body Work','Preventive Maintenance','DOT Inspection','General Repair'];

export default function Vendors() {
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ name:'',contact_name:'',email:'',phone:'',address:'',city:'',us_state:'',specialties:[] });
  const [saving, setSaving] = useState(false);

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { load(); }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendors', { params: { search: search || undefined } });
      setVendors(r.data);
    } catch { toast.error('Failed to load vendors'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/vendors', form);
      toast.success('Vendor added!');
      setShowCreate(false);
      setForm({ name:'',contact_name:'',email:'',phone:'',address:'',city:'',us_state:'',specialties:[] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/vendors/import/preview', fd);
      setPreview(r.data);
      setMapping(r.data.auto_mapping || {});
      toast.success(`Parsed ${r.data.total_rows} rows — review column mapping below`);
    } catch (e) {
      toast.error('Could not parse file: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleImportConfirm = async () => {
    setImporting(true);
    try {
      const r = await api.post('/vendors/import/confirm', { rows: preview.preview.slice(0, 500), mapping });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.created} vendors!`);
      load();
    } catch (e) {
      toast.error('Import failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  const toggleSpec = (s) => setForm(p => ({ ...p, specialties: p.specialties.includes(s) ? p.specialties.filter(x => x !== s) : [...p.specialties, s] }));

  return (
    <div className="space-y-5 animate-fade-in" data-testid="vendors-page">
      <div className="flex items-center justify-between pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// VENDOR NETWORK</p>
          <h1 className="font-['Manrope'] font-extrabold text-2xl" style={{ color: 'var(--text-primary)' }}>Vendor Directory</h1>
          <p className="font-['IBM_Plex_Sans'] text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{vendors.length} vendors</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowImport(true)} data-testid="import-vendors-btn"
            className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm transition-colors font-['IBM_Plex_Sans']"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
            <Upload size={14} /> Import CSV / Excel
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="add-vendor-btn"
            className="btn-primary flex items-center gap-2 text-sm uppercase tracking-wide">
            <Plus size={14} /> Add Vendor
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or city..."
          className="w-full input-slate pl-9" />
      </div>

      <div className="card-slate overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div> : (
          <table className="w-full table-comfortable">
            <thead>
              <tr>
                {['NAME','CONTACT','PHONE','EMAIL','LOCATION','SPECIALTIES','PRICING'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16 font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>
                  No vendors yet. Add one or import from CSV/Excel.
                </td></tr>
              ) : vendors.map((v) => (
                <tr key={v.id}>
                  <td className="font-['Manrope'] font-bold" style={{ color: 'var(--text-primary)' }}>{v.name}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{v.contact_name || '—'}</td>
                  <td className="font-['JetBrains_Mono'] text-xs" style={{ color: 'var(--text-muted)' }}>{v.phone || '—'}</td>
                  <td className="text-xs" style={{ color: 'var(--text-muted)' }}>{v.email || '—'}</td>
                  <td className="text-xs" style={{ color: 'var(--text-muted)' }}>{[v.city, v.us_state].filter(Boolean).join(', ') || '—'}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {v.specialties?.slice(0,3).map(s => (
                        <span key={s} className="px-1.5 py-0.5 rounded-full text-[9px] font-['JetBrains_Mono']" style={{ background: 'var(--primary-muted)', color: 'var(--primary)', border: '1px solid var(--primary)' }}>{s}</span>
                      ))}
                      {v.specialties?.length > 3 && <span className="text-[10px]" style={{ color: 'var(--text-faint)' }}>+{v.specialties.length - 3}</span>}
                    </div>
                  </td>
                  <td>
                    <button onClick={e => { e.stopPropagation(); navigate(`/ops/vendors/${v.id}/pricing`); }}
                      className="flex items-center gap-1 px-2.5 py-1 text-[9px] font-bold uppercase border rounded-md transition-colors font-['JetBrains_Mono']"
                      style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-muted)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <DollarSign size={10} /> Pricing
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Vendor Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-lg">
          <DialogHeader><DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Add Vendor</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              {[['Vendor / Shop Name *','name','text',true,2],['Contact Person','contact_name','text',false,1],['Phone','phone','tel',false,1],['Email','email','email',false,1],['City','city','text',false,1],['State','us_state','text',false,1]].map(([label,key,type,req,cols]) => (
                <div key={key} className={cols === 2 ? 'col-span-2' : ''}>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>{label}</label>
                  <input type={type} required={req} value={form[key]} onChange={e => setForm(p => ({...p,[key]:e.target.value}))}
                    className="w-full input-slate" />
                </div>
              ))}
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Specialties</label>
              <div className="flex flex-wrap gap-2">
                {SPECIALTIES.map(s => (
                  <button key={s} type="button" onClick={() => toggleSpec(s)}
                    className="px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all font-['JetBrains_Mono']"
                    style={{ 
                      background: form.specialties.includes(s) ? 'var(--primary-muted)' : 'transparent',
                      borderColor: form.specialties.includes(s) ? 'var(--primary)' : 'var(--border)',
                      color: form.specialties.includes(s) ? 'var(--primary)' : 'var(--text-faint)'
                    }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide">
              {saving && <Loader2 size={14} className="animate-spin" />} Add Vendor
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImport} onOpenChange={v => { setShowImport(v); if (!v) { setPreview(null); setImportResult(null); setImportFile(null); } }}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-2xl">
          <DialogHeader><DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Import Vendors from CSV / Excel</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-4 border border-dashed rounded-md text-center" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
              <Upload size={24} className="mx-auto mb-2" style={{ color: 'var(--text-faint)' }} />
              <p className="font-['IBM_Plex_Sans'] text-sm mb-3" style={{ color: 'var(--text-muted)' }}>Upload your vendor spreadsheet</p>
              <p className="font-['JetBrains_Mono'] text-xs mb-3" style={{ color: 'var(--text-faint)' }}>Supports .csv, .xlsx, .xls — columns auto-detected</p>
              <label className="cursor-pointer px-4 py-2 border rounded-md text-sm transition-colors font-['IBM_Plex_Sans']"
                style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                Choose File
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" data-testid="import-file-input" />
              </label>
              {importFile && <p className="mt-2 text-xs font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>{importFile.name}</p>}
            </div>

            {preview && !importResult && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />
                  <p className="font-['JetBrains_Mono'] text-xs" style={{ color: 'var(--success)' }}>File parsed: {preview.total_rows} rows, {preview.headers.length} columns</p>
                </div>
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>COLUMN MAPPING (auto-detected — adjust if needed)</p>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {['name','contact_name','email','phone','address','city','us_state','specialties'].map(field => (
                      <div key={field} className="flex items-center gap-2">
                        <span className="text-[10px] font-['JetBrains_Mono'] w-24 flex-shrink-0" style={{ color: 'var(--primary)' }}>{field}</span>
                        <select value={mapping[field] || ''} onChange={e => setMapping(p => ({ ...p, [field]: e.target.value || undefined }))}
                          className="flex-1 h-8 px-2 rounded-md text-xs font-['IBM_Plex_Sans'] focus:outline-none"
                          style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                          <option value="">— skip —</option>
                          {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto max-h-32 border rounded-md" style={{ borderColor: 'var(--border)' }}>
                  <table className="text-xs font-['JetBrains_Mono'] min-w-full" style={{ color: 'var(--text-muted)' }}>
                    <thead><tr className="border-b" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                      {preview.headers.map(h => <th key={h} className="px-3 py-1.5 text-left text-[9px] uppercase whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>{preview.preview.map((row, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: 'var(--border)' }}>
                        {preview.headers.map(h => <td key={h} className="px-3 py-1.5 whitespace-nowrap max-w-[120px] truncate">{String(row[h] || '')}</td>)}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <button onClick={handleImportConfirm} disabled={importing} className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide">
                  {importing ? <><Loader2 size={14} className="animate-spin" />Importing...</> : `Import ${Math.min(preview.total_rows, 500)} Vendors`}
                </button>
              </div>
            )}

            {importResult && (
              <div className="p-4 rounded-md flex items-center gap-3" style={{ background: 'var(--success-muted)', border: '1px solid var(--success)' }}>
                <CheckCircle2 size={18} style={{ color: 'var(--success)' }} />
                <div>
                  <p className="font-['Manrope'] font-bold" style={{ color: 'var(--success)' }}>Import Complete!</p>
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>{importResult.created} vendors imported{importResult.errors?.length > 0 ? `, ${importResult.errors.length} skipped` : ''}</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
